import * as React from 'react';
import { Popover } from 'antd';
import { FI18n } from '@freelog/tools-lib';
import FIcons from '@/components/FIcons';
import styles from './index.less';
import type { Option, SelectedCache } from './types';

interface CascaderMenuProps {
  options: Option[];
  showAddNewType: boolean;
  parentOption?: Option;
  zIndex?: number;
  getPopupContainer: () => HTMLElement;
  onSelect: (v: { value: string; labels: string[] }) => void;
  onClickCustom: (cache: SelectedCache) => void;
  onFocusInput: () => void;
}

function CascaderMenuItem({
  option,
  showAddNewType,
  parentOption,
  zIndex,
  isLast,
  getPopupContainer,
  onSelect,
  onClickCustom,
  onFocusInput,
}: {
  option: Option;
  showAddNewType: boolean;
  parentOption?: Option;
  zIndex: number;
  isLast: boolean;
  getPopupContainer: () => HTMLElement;
  onSelect: (v: { value: string; labels: string[] }) => void;
  onClickCustom: (cache: SelectedCache) => void;
  onFocusInput: () => void;
}) {
  const parentContentRef = React.useRef<HTMLDivElement>(null);
  const hasChildren = option.children.length > 0;

  const itemContent = (
    <div
      className={styles.item}
      onClick={(e) => {
        // 阻止冒泡到触发器，避免选中后下拉被重新打开
        e.stopPropagation();
        if (!hasChildren) {
          onSelect({ value: option.value, labels: option.labels });
        } else {
          onFocusInput();
        }
      }}
    >
      {parentOption === undefined ? (
        <div className={styles.itemFirstCatalogue}>
          <div className={styles.itemFirstCatalogue_name}>{option.label}</div>
          {option.note && (
            <>
              <div style={{ height: 2 }} />
              <div className={styles.itemFirstCatalogue_note}>{option.note}</div>
            </>
          )}
        </div>
      ) : (
        <span>{option.label}</span>
      )}
      {hasChildren && <FIcons.FRight className={styles.itemRightIcon} />}
    </div>
  );

  return (
    <>
      {hasChildren ? (
        <Popover
          arrow={false}
          align={{ offset: [5, 0] }}
          zIndex={zIndex}
          getPopupContainer={getPopupContainer}
          open={undefined}
          classNames={{ root: styles.PopoverOverlayClassName }}
          title={null}
          placement="rightTop"
          content={
            <div ref={parentContentRef} className={styles.itemChildren}>
              <CascaderMenu
                options={option.children}
                showAddNewType={showAddNewType}
                parentOption={option}
                zIndex={zIndex + 1}
                getPopupContainer={() =>
                  parentContentRef.current || getPopupContainer()
                }
                onSelect={onSelect}
                onClickCustom={onClickCustom}
                onFocusInput={onFocusInput}
              />
            </div>
          }
        >
          {itemContent}
        </Popover>
      ) : (
        itemContent
      )}

      {showAddNewType && isLast && parentOption && (
        <div
          className={`${styles.item} ${styles.itemLatest}`}
          onClick={(e) => {
            e.stopPropagation();
            onClickCustom({
              value: parentOption.value,
              label: parentOption.label,
              values: parentOption.values,
              labels: parentOption.labels,
            });
          }}
        >
          <span>
            {FI18n.i18nNext.tAuto('createresource_selectresourcetype_btn_addnewtype')}
          </span>
        </div>
      )}
    </>
  );
}

function CascaderMenu({
  options,
  showAddNewType,
  parentOption,
  zIndex = 10000,
  getPopupContainer,
  onSelect,
  onClickCustom,
  onFocusInput,
}: CascaderMenuProps) {
  return (
    <>
      {options.map((option, index) => (
        <React.Fragment key={option.value}>
          <CascaderMenuItem
            option={option}
            showAddNewType={showAddNewType}
            parentOption={parentOption}
            zIndex={zIndex}
            isLast={index + 1 === options.length}
            getPopupContainer={getPopupContainer}
            onSelect={onSelect}
            onClickCustom={onClickCustom}
            onFocusInput={onFocusInput}
          />
        </React.Fragment>
      ))}
    </>
  );
}

export default CascaderMenu;
